import { Download, ExternalLink, Loader2 } from 'lucide-react';
import type { JobFileEntry } from '@jobos/shared';

import { FileViewerShell } from '@/components/shared/FileViewerShell';
import { Button } from '@/components/ui/button';
import { useJobFileUrl } from '@/hooks/useJobFiles';
import { iconForJobFile, isPdfEntry } from './jobFileDisplay';
import { useJobFileDownload } from './useJobFileDownload';

// The viewer for everything the lightbox can't show. A PDF renders in an iframe
// off its inline presigned URL; anything else says plainly that it can't be
// previewed and offers the file itself.
//
// Shares `FileViewerShell` with the photo viewer, so a document opens in the
// same window a photo does.
//
// Office preview is deliberately absent: rendering it server-side would need
// LibreOffice in the container competing with the PDF renderer for memory, and
// Microsoft's hosted viewer needs a publicly reachable URL, which is the
// opposite of presigned access. A clear sentence beats a frame that fails.

interface JobFileDocumentViewerProps {
  jobId: string;
  /** The file to show; null closes the dialog. */
  file: JobFileEntry | null;
  onClose: () => void;
}

export function JobFileDocumentViewer({ jobId, file, onClose }: JobFileDocumentViewerProps) {
  const open = file != null;
  const isPdf = file != null && isPdfEntry(file);
  const download = useJobFileDownload(jobId);

  // `display` is the inline variant — `/url` sets no content disposition, and the
  // R2 object carries its real content type, so the browser renders it. Only a
  // PDF has anything to render, so nothing else pays for the presign.
  const { data: url, isError } = useJobFileUrl(jobId, file?.id, 'display', { enabled: isPdf });

  // iOS Safari renders only the first page of a PDF inside an iframe and will not
  // scroll it. Handing the same URL to a tab gets the native viewer, which
  // scrolls, zooms and shares. On desktop it is simply a bigger window.
  const handleOpenInNewTab = () => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const Icon = file ? iconForJobFile(file) : null;

  return (
    <FileViewerShell
      open={open}
      onClose={onClose}
      title={file?.fileName ?? ''}
      bodyClassName="bg-gray-100"
      actions={
        file && (
          <>
            {isPdf && (
              <Button
                variant="outline"
                size="sm"
                disabled={!url}
                onClick={handleOpenInNewTab}
                className="shrink-0"
              >
                <ExternalLink className="h-4 w-4" />
                Open in new tab
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => download(file.id)}
              className="shrink-0"
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
          </>
        )
      }
    >
      {file &&
        (isPdf ? (
          isError ? (
            <p className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-600">
              Couldn't load this document. It may still be uploading — try again shortly.
            </p>
          ) : url ? (
            <iframe src={url} title={file.fileName} className="h-full w-full border-0 bg-white" />
          ) : (
            <div className="flex h-full items-center justify-center">
              <Loader2
                data-testid="job-file-document-loading"
                aria-label="Loading document"
                className="h-8 w-8 animate-spin text-gray-400"
              />
            </div>
          )
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            {Icon && <Icon className="h-12 w-12 text-gray-400" />}
            <p className="text-sm font-medium text-gray-900">{file.fileName}</p>
            <p className="max-w-sm text-sm text-gray-600">
              Preview isn't available for this file type. Download it to open on your device.
            </p>
            {/* Repeated from the header on purpose: downloading is the only thing
                to do here, so it should not be a corner afterthought. */}
            <Button onClick={() => download(file.id)} className="mt-1">
              <Download className="h-4 w-4" />
              Download
            </Button>
          </div>
        ))}
    </FileViewerShell>
  );
}
